import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@invent/wl-ui-kit-next/ui';
import { ChevronsUpDown } from 'lucide-react';
import * as React from 'react';

import { ParticipantAvatar } from '@/design';
import { errorMessage } from '@/domains/api';
import type {
  IAdminParticipantListItem,
  IAdminPersonRow,
  ISearchMatch
} from '../api/participant-contracts';
import { participantsApi } from '../api/participants-api';
import { registryNumbers } from '../registries';
import { RECORD_TYPE_WORDS } from './participant-link';
import { MatchLine } from './search-matches';

/**
 * A person picked from People — the roster's "who sits in this slot" (SR-222, F7's R5, the
 * roadmap's U13): a typeahead over `/admin/people?q=`, the command palette's shape, inside a
 * popover so it fits a table cell.
 *
 * **In the admin domain, not `src/design`**: it reads the People reader, and the design system
 * imports no API module (`scripts/check-zones.mjs`).
 *
 * **The search is survey-api's, and the picker chooses which of its answers can be chosen.**
 * One box, a quarter-second after the typing stops, from two characters, the previous read
 * cancelled — the palette's delay and minimum, so a name or a CRD finds the same people in both —
 * ten rows rather than its five. A People row is an account and every person it acts for — since
 * SR-414 People holds persons only (record search D3), so every participant of a row is offered,
 * each once. It asks no `include`: a roster row has no participant to pick. survey-api's 422
 * (`roster-member-not-person`) stays the judge.
 *
 * Each person is told apart by what two of one name differ in: their registry numbers and their
 * row's account address. "Nobody" and, where the caller offers it, the empty choice (`none`) are
 * always above the results.
 *
 * **A ranking's insert reuses it** (SR-231): with `everyParticipant` the search is survey-api's
 * participant list (`/admin/participants?q=`) rather than People — every participant, a team or
 * firm no account acts for included, since a TEAMS or RIA_FIRM ranking places them — each with its
 * record type beside a team's or a firm's name ("Team", "Firm", "Organization" — survey-api's
 * `recordType`, SR-414) and the first reason it matched (`MatchLine`); with no `nobody` there is no
 * Decision group.
 */

/** What the picker holds: a person, or Nobody — `null` is the empty choice, no pick at all. */
export type ParticipantPick =
  | { kind: 'person'; participantId: string; displayName: string; photoUri: Nullable<string> }
  | { kind: 'nobody' };

type Found =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'done'; options: IPersonOption[] }
  | { state: 'failed'; message: string };

interface IPersonOption {
  participantId: string;
  displayName: string;
  photoUri: Nullable<string>;
  detail: string;
  /** Why the search found it — the participant list's first match; none from People. */
  match?: ISearchMatch;
}

const MIN_QUERY = 2;
const LIMIT = 10;
const SETTLE_MS = 250;

export function ParticipantPicker({
  label,
  value,
  onChange,
  disabled,
  initialQuery,
  nobody,
  none,
  everyParticipant = false,
  placeholder = 'No decision'
}: {
  /** The control's accessible name. */
  label: string;
  value: Nullable<ParticipantPick>;
  onChange: (next: Nullable<ParticipantPick>) => void;
  disabled?: boolean;
  /** What the box holds as it opens — the likeliest person's number or name. */
  initialQuery: string;
  /** The words for Nobody; no Decision group when absent. */
  nobody?: string;
  /** The words for the empty choice; not offered when absent. */
  none?: string;
  /** Search every participant, not People's persons. */
  everyParticipant?: boolean;
  /** What the button says while nothing is picked. */
  placeholder?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState(initialQuery);
  const [settled, setSettled] = React.useState('');
  const [found, setFound] = React.useState<Found>({ state: 'idle' });
  // The option Enter picks. cmdk highlights the first, which is Nobody — so once a search answers,
  // its first person is highlighted instead: a CRD typed and Enter pressed picks its holder. Until
  // the search for what the box holds has answered, Enter picks nothing (`holdEnter`): the
  // highlight is still Nobody or the last search's first person, and either would be a wrong pick.
  const [active, setActive] = React.useState('');

  const onOpenChange = (next: boolean) => {
    if (next) {
      // Searched at once, so a reopened picker never runs the last visit's query first.
      setQuery(initialQuery);
      setSettled(initialQuery.trim());
    }
    setOpen(next);
  };

  const holdEnter = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') return;
    if (query.trim() !== settled || found.state === 'loading') event.preventDefault();
  };

  React.useEffect(() => {
    const timer = setTimeout(() => setSettled(query.trim()), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  React.useEffect(() => {
    if (!open || settled.length < MIN_QUERY) {
      setFound({ state: 'idle' });
      return undefined;
    }
    const controller = new AbortController();
    setFound({ state: 'loading' });
    const search = everyParticipant
      ? participantsApi
          .listParticipants({ q: settled, pageSize: LIMIT }, controller.signal)
          .then((page) => page.items.map(participantOption))
      : participantsApi
          .listPeople({ q: settled, pageSize: LIMIT }, controller.signal)
          .then((page) => personsOf(page.items));
    search
      .then((persons) => {
        // A read the next query cancelled can still have its body in hand; its answer is not kept.
        if (controller.signal.aborted) return;
        setFound({ state: 'done', options: persons });
        if (persons.length > 0) setActive(optionValue(persons[0]));
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setFound({ state: 'failed', message: errorMessage(error, 'People could not be searched') });
        }
      });
    return () => controller.abort();
  }, [open, settled, everyParticipant]);

  const choose = (next: Nullable<ParticipantPick>) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          disabled={disabled}
          className="h-auto min-h-9 w-full min-w-48 justify-between gap-2 font-normal"
        >
          <PickedLine value={value} nobody={nobody ?? ''} placeholder={placeholder} />
          <ChevronsUpDown className="size-4 shrink-0 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(26rem,calc(100vw-2rem))] p-0">
        <Command
          shouldFilter={false}
          label="Search People"
          value={active}
          onValueChange={setActive}
        >
          <CommandInput
            value={query}
            onValueChange={setQuery}
            onKeyDown={holdEnter}
            placeholder="A name, an address or a number"
          />
          <CommandList className="max-h-[min(22rem,60dvh)] py-1">
            {nobody !== undefined && (
              <CommandGroup heading="Decision">
                <CommandItem value="pick:nobody" onSelect={() => choose({ kind: 'nobody' })}>
                  {nobody}
                </CommandItem>
                {none !== undefined && (
                  <CommandItem value="pick:none" onSelect={() => choose(null)}>
                    {none}
                  </CommandItem>
                )}
              </CommandGroup>
            )}
            <CommandGroup heading="People">
              <FoundLines
                found={found}
                q={settled}
                onPick={(option) =>
                  choose({
                    kind: 'person',
                    participantId: option.participantId,
                    displayName: option.displayName,
                    photoUri: option.photoUri
                  })
                }
              />
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function PickedLine({
  value,
  nobody,
  placeholder
}: {
  value: Nullable<ParticipantPick>;
  nobody: string;
  placeholder: string;
}) {
  if (value === null) return <span className="text-muted-foreground">{placeholder}</span>;
  if (value.kind === 'nobody') return <span>{nobody}</span>;
  return (
    <span className="flex min-w-0 items-center gap-2">
      <ParticipantAvatar
        photoUri={value.photoUri}
        displayName={value.displayName}
        size="sm"
        decorative
      />
      <span className="truncate">{value.displayName}</span>
    </span>
  );
}

function FoundLines({
  found,
  q,
  onPick
}: {
  found: Found;
  /** The search answered — whose words a match line marks. */
  q: string;
  onPick: (option: IPersonOption) => void;
}) {
  const line = (text: string, tone?: 'failed') => (
    <p
      role={tone ? 'alert' : undefined}
      className={cn('px-2 py-2 text-xs text-muted-foreground', tone && 'text-destructive')}
    >
      {text}
    </p>
  );
  switch (found.state) {
    case 'idle':
      return line('Type at least two characters to search People.');
    case 'loading':
      return line('Searching…');
    case 'failed':
      return line(found.message, 'failed');
    case 'done':
      return (
        <>
          {found.options.length === 0 && line('No person matches.')}
          {found.options.map((option) => (
            <CommandItem
              key={option.participantId}
              value={optionValue(option)}
              onSelect={() => onPick(option)}
            >
              <ParticipantAvatar
                photoUri={option.photoUri}
                displayName={option.displayName}
                size="sm"
                decorative
              />
              <div className="min-w-0 flex-1">
                <span className="block truncate font-medium">{option.displayName}</span>
                {option.detail && (
                  <span className="block truncate text-xs text-muted-foreground">
                    {option.detail}
                  </span>
                )}
                {option.match && (
                  <div className="text-xs text-muted-foreground [&_p]:truncate">
                    <MatchLine match={option.match} q={q} linked={false} />
                  </div>
                )}
              </div>
            </CommandItem>
          ))}
        </>
      );
  }
}

function optionValue(option: IPersonOption): string {
  return `person:${option.participantId}`;
}

/** One row of the participant list as an option — a team's or a firm's record type said beside
 *  it (its `kind` where an older API sends no type), and the first reason it matched. */
function participantOption(row: IAdminParticipantListItem): IPersonOption {
  return {
    participantId: row.participantId,
    displayName: row.displayName,
    photoUri: row.photoUri,
    match: row.matches?.[0],
    detail: [
      typeWords(row),
      ...registryNumbers(row.identifiers).map((id) => id.value),
      row.profile.firmName
    ]
      .filter(Boolean)
      .join(', ')
  };
}

/** A row's record type in words — nothing for a person, its `kind` as spelled where the API sends
 *  no type. */
function typeWords(row: IAdminParticipantListItem): string {
  const type = row.recordType ?? null;
  if (type === null) return row.kind === 'person' ? '' : row.kind;
  return type === 'person' ? '' : RECORD_TYPE_WORDS[type];
}

/** The persons of a page of People rows, in survey-api's order, each once — a participant two
 *  accounts act for is on both rows. */
function personsOf(rows: IAdminPersonRow[]): IPersonOption[] {
  const options: IPersonOption[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const participant of row.participants) {
      if (seen.has(participant.participantId)) continue;
      seen.add(participant.participantId);
      options.push({
        participantId: participant.participantId,
        displayName: participant.displayName,
        photoUri: participant.photoUri,
        detail: [...registryNumbers(participant.identifiers).map((id) => id.value), row.email]
          .filter(Boolean)
          .join(', ')
      });
    }
  }
  return options;
}
